import React, { useCallback, useMemo } from "react";
import { SearchOption, TypeaheadInput, AgGridReact, AgGrid, toast } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { useGetArenasLazyQuery, useGetTreasuryArenasLazyQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { filterText } from "../styles_vanilla.css";
import { ArenaEdgeType } from "./types";

export const ClientFilter = (filterParams: AgGridReact.CustomFilterProps) => {
  const { onModelChange, getValue } = filterParams;
  const treasuryClientLevelPermissions = useFeatureFlag(
    "treasuryClientLevelPermissions",
    false,
  );

  // Both hooks must be called unconditionally; active branch is selected below.
  const [fetchArenas, arenasResult] = useGetArenasLazyQuery();
  const [fetchTreasuryArenas, treasuryArenasResult] = useGetTreasuryArenasLazyQuery();

  const fetchFn = treasuryClientLevelPermissions ? fetchTreasuryArenas : fetchArenas;
  const loading = treasuryClientLevelPermissions
    ? treasuryArenasResult.loading
    : arenasResult.loading;

  const handleSelection = useCallback(
    (value?: SearchOption) => {
      onModelChange(value?.id);
    },
    [onModelChange],
  );

  const arenaList = useMemo(() => {
    const edges = treasuryClientLevelPermissions
      ? treasuryArenasResult.data?.treasuryArenas?.edges
      : arenasResult.data?.arenas?.edges;
    return (
      edges?.map((arena: ArenaEdgeType) => ({
        id: arena.node.id,
        label: arena.node.name,
      })) ?? []
    );
  }, [arenasResult.data, treasuryArenasResult.data, treasuryClientLevelPermissions]);

  const handleDebouncedSearch = useCallback(
    async (value?: string) => {
      if (value?.length) {
        try {
          // useLazyQuery resolves even on Apollo errors — inspect result.error
          // explicitly so auth failures on the permission-scoped treasury path
          // are reported rather than silently producing an empty dropdown.
          const result = await fetchFn({
            context: clientContext(),
            variables: { searchTerm: value },
            fetchPolicy: "no-cache",
          });
          if (result.error) {
            addError(formatError(result.error));
            toast.error("Failed to load clients. Please try again.");
          }
        } catch (err) {
          addError(formatError(err));
          toast.error("Failed to load clients. Please try again.");
        }
      } else {
        onModelChange(null);
      }
    },
    [fetchFn, onModelChange],
  );

  // Note: doesFilterPass is only called with the Client-Side Row Model.
  const doesFilterPass = useCallback(
    (params: AgGrid.IDoesFilterPassParams) => {
      return getValue(params.node) == filterParams.model;
    },
    [getValue, filterParams],
  );

  // register filter callbacks with the grid
  AgGridReact.useGridFilter({ doesFilterPass });
  return (
    <TypeaheadInput
      className={filterText}
      data-testid="client-filter"
      isClearable
      isFullWidth
      isLoading={loading}
      options={arenaList}
      onSelectOption={handleSelection}
      onDebouncedSearch={handleDebouncedSearch}
      placeholder="Search for a client"
    />
  );
};
